import { useCallback, useEffect, useMemo, useRef } from 'react'
import { useQuery } from '@tanstack/react-query'

import { getTimeEntries } from '../../../../api/commonApi'
import type { TimeEntryLike } from '@/features/timeEntries/types'
import { buildDailyTimeEntriesQuery } from '../utils/buildDailyTimeEntriesQuery'

export type UseDailyTimeEntriesParams = {
  dateKey?: string | null
  selectedUser?: {
    id?: string | number
    [key: string]: any
  } | null
  enabled?: boolean
  refreshKey?: any
}

const EMPTY_TIME_ENTRIES: TimeEntryLike[] = []

export const getDailyTimeEntriesQueryKey = (
  dateKey?: string | null,
  userId?: string | number
) =>
  [
    'daily-time-entries',
    {
      dateKey,
      userId,
    },
  ] as const

export const fetchDailyTimeEntries = async (
  dateKey: string,
  userId?: string | number
): Promise<TimeEntryLike[]> => {
  const query = buildDailyTimeEntriesQuery(dateKey, userId)
  const entries = await getTimeEntries(query)
  return entries ?? []
}

export default function useDailyTimeEntries({
  dateKey,
  selectedUser,
  enabled = true,
  refreshKey,
}: UseDailyTimeEntriesParams) {
  const refreshKeyRef = useRef(refreshKey)
  const userId = selectedUser?.id
  const requestScopeRef = useRef(`${dateKey ?? ''}:${userId ?? ''}`)
  const queryKey = useMemo(
    () => getDailyTimeEntriesQueryKey(dateKey, userId),
    [dateKey, userId]
  )
  const queryEnabled = enabled && Boolean(dateKey)

  const queryFn = useCallback(async (): Promise<TimeEntryLike[]> => {
    if (!dateKey) return []
    return fetchDailyTimeEntries(dateKey, userId)
  }, [dateKey, userId])

  const { data, dataUpdatedAt, isFetching, refetch } = useQuery({
    queryKey,
    queryFn,
    enabled: queryEnabled,
  })

  const refetchDailyTimeEntries = useCallback(async () => {
    if (!queryEnabled) return []
    const result = await refetch()
    return result.data ?? []
  }, [queryEnabled, refetch])

  useEffect(() => {
    if (refreshKeyRef.current === refreshKey) return
    refreshKeyRef.current = refreshKey
    refetchDailyTimeEntries().catch(() => undefined)
  }, [refetchDailyTimeEntries, refreshKey])

  useEffect(() => {
    const requestScope = `${dateKey ?? ''}:${userId ?? ''}`
    if (requestScopeRef.current === requestScope) return
    requestScopeRef.current = requestScope
    refetchDailyTimeEntries().catch(() => undefined)
  }, [dateKey, refetchDailyTimeEntries, userId])

  return {
    timeEntries: data ?? EMPTY_TIME_ENTRIES,
    dataUpdatedAt,
    loading: isFetching && data === undefined,
    refreshing: isFetching && data !== undefined,
    refetchDailyTimeEntries,
  }
}
